import { Component, computed, inject, OnInit, output, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ButtonComponent,
  DatePickerComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormFieldComponent,
  MessageComponent,
} from '@akostm/orbit-ui';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { ApiKeyListItem } from '@clientwire/api';

export interface ApiKeyFormDialogResult {
  eventType: 'SAVE' | 'CANCEL';
  data?: ApiKeyData;
}

export interface ApiKeyData {
  name: string;
  expiresAt: Date;
}

@Component({
  selector: 'lib-api-keys-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    FormFieldComponent,
    MessageComponent,
    DatePickerComponent,
  ],
  templateUrl: './api-keys-dialog.component.html',
  styleUrl: './api-keys-dialog.component.scss',
})
export class ApiKeysDialogComponent implements OnInit {
  dialogButtonClicked = output<ApiKeyFormDialogResult>();

  readonly saveIcon = faSave;
  public form!: FormGroup;
  readonly isConfirmation = signal(false);
  readonly errorMessage = signal<string | undefined>(undefined);
  minimumDate = signal(new Date());

  public data: {
    title: string;
    apiKey?: ApiKeyListItem;
    error: WritableSignal<string>;
  } = inject(DIALOG_DATA);
  private fb: FormBuilder = inject(FormBuilder);
  private translateService = inject(TranslateService);

  // Translation parameters for the date picker
  todayLabel = computed(() => this.translateService.instant('DATE_PICKER.TODAY'));
  clearLabel = computed(() => this.translateService.instant('DATE_PICKER.CLEAR'));
  monthNames = computed(() => this.translateService.instant('DATE_PICKER.MONTHS'));
  weekdayNames = computed(() => this.translateService.instant('DATE_PICKER.WEEK_DAYS'));

  ngOnInit(): void {
    if (this.data.error) {
      this.errorMessage.set(this.data.error());
    }
    this.form = this.fb.group({
      name: [this.data.apiKey?.name ?? '', [Validators.required]],
      expiresAt: [this.data.apiKey?.expiresAt],
    });
  }

  onSubmit() {
    if (this.form.valid || this.isConfirmation()) {
      this.dialogButtonClicked.emit({
        eventType: 'SAVE',
        data: this.form.value,
      });
    }
  }

  onCancel() {
    this.dialogButtonClicked.emit({
      eventType: 'CANCEL',
    });
  }

  get name() {
    return this.form.get('name') as FormControl<string>;
  }
}
